import { useEffect, useRef, useState } from "react";
import { getGroupChannelPresence, updateGroupChannelPresence } from "../services/groupService";

/**
 * useGroupPresence
 * Handles presence activation, heartbeat, away cleanup, and snapshot fetch for a group channel.
 *
 * @param {Object} params
 * @param {string|number} params.groupId
 * @param {string|number} params.channelId
 * @param {boolean} [params.active=true] - Whether the composer/chat is active (chat tab selected).
 * @returns {{ snapshot: { onlineUserIds: string[], updatedAt?: string }, isAvailable: boolean }}
 */
export default function useGroupPresence({ groupId, channelId, active = true }) {
  const [snapshot, setSnapshot] = useState({ onlineUserIds: [] });
  const [isAvailable, setIsAvailable] = useState(false);
  const hbRef = useRef(null);

  // Reset snapshot on channel change
  useEffect(() => {
    setSnapshot({ onlineUserIds: [] });
  }, [groupId, channelId]);

  // Activation + heartbeat when active
  useEffect(() => {
    if (!groupId || !channelId) {
      return undefined;
    }

    if (!active) {
      // Not in chat tab: fetch presence snapshot passively
      let cancelled = false;
      (async () => {
        try {
          const s = await getGroupChannelPresence(groupId, channelId);
          if (!cancelled) {
            setSnapshot({ onlineUserIds: s?.onlineUserIds || [], updatedAt: s?.updatedAt });
            setIsAvailable(true);
          }
        } catch (_) {
          if (!cancelled) setIsAvailable(false);
        }
      })();
      return () => { cancelled = true; };
    }

    let cancelled = false;
    (async () => {
      try {
        const s = await updateGroupChannelPresence(groupId, channelId, "ACTIVE");
        if (!cancelled) {
          setSnapshot({ onlineUserIds: s?.onlineUserIds || [], updatedAt: s?.updatedAt });
          setIsAvailable(true);
        }
      } catch (e) {
        if (!cancelled) setIsAvailable(false);
      }
    })();

    const id = window.setInterval(() => {
      updateGroupChannelPresence(groupId, channelId, "ACTIVE").catch(() => {});
    }, 45000);
    hbRef.current = id;

    return () => {
      cancelled = true;
      if (hbRef.current) {
        clearInterval(hbRef.current);
        hbRef.current = null;
      }
      updateGroupChannelPresence(groupId, channelId, "AWAY").catch(() => {});
    };
  }, [groupId, channelId, active]);

  return { snapshot, isAvailable };
}
